import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, switchMap, takeWhile, timer } from 'rxjs';

import { environment } from '../../../../environments/environment';

import {
  AiTokenProduct,
  AiTokenPurchaseResult,
  AiUsage,
  AiUsageSummary,
  AnalysisBatch,
  BulkAnalysisRequest,
  ContractAnalysis,
  ContractAiShareReportRequest,
  ContractAiShareReportResponse,
} from '../../model';

@Injectable({ providedIn: 'root' })
export class ContractAiAnalysisService {
  private readonly base: string = `${environment.apiEndpoint}/ai/analysis`;
  private readonly usageUrl: string = `${environment.apiEndpoint}/ai/usage/summary`;
  private readonly tokensBase: string = `${environment.apiEndpoint}/account/ai-tokens`;

  constructor(private http: HttpClient) {}

  /** Advisor's AI token balance (used / acquired limit / plan label). */
  public getUsage(): Observable<AiUsage> {
    return this.http.get<AiUsageSummary>(this.usageUrl).pipe(
      map((summary: AiUsageSummary): AiUsage => ({
        used: summary.usedTokens,
        limit: summary.quotaTokens,
        planTier: summary.plan,
      })),
    );
  }

  /** The one-time AI token package + the saved card it would be charged to (for the buy dialog). */
  public getTokenProduct(): Observable<AiTokenProduct> {
    return this.http.get<AiTokenProduct>(`${this.tokensBase}/product`);
  }

  /**
   * Buys the one-time token package. The backend charges the saved default card off-session; if it
   * needs interactive confirmation (3-D Secure) or there is no saved card, the result is REDIRECT
   * with a Stripe-hosted `checkoutUrl` to open. `returnUrl` is where Checkout returns on that path.
   */
  public purchaseTokens(returnUrl: string): Observable<AiTokenPurchaseResult> {
    const params = new HttpParams().set('return_url', returnUrl);
    return this.http.post<AiTokenPurchaseResult>(`${this.tokensBase}/purchase`, null, { params });
  }

  /**
   * Starts an async analysis (the model call can take up to ~2 minutes). Returns 202 immediately
   * with a RUNNING handle. Completion arrives via the `ai/analysis-complete` WebSocket action;
   * {@link pollAnalysis} is the fallback. Counts against the AI quota.
   */
  public runForClient(clientId: string): Observable<ContractAnalysis> {
    return this.http.post<ContractAnalysis>(`${this.base}/clients/${clientId}`, {});
  }

  /** Latest stored analysis. Emits a 404 error if the client was never analysed. */
  public getLatest(clientId: string): Observable<ContractAnalysis> {
    return this.http.get<ContractAnalysis>(`${this.base}/clients/${clientId}/latest`);
  }

  /**
   * Polls one analysis every `intervalMs` until it leaves RUNNING/PENDING, emitting each snapshot
   * (the final emission is terminal). Fallback for when the WebSocket completion event is missed.
   */
  public pollAnalysis(analysisUid: string, intervalMs = 4000): Observable<ContractAnalysis> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getById(analysisUid)),
      takeWhile((analysis): boolean => analysis.status === 'RUNNING' || analysis.status === 'PENDING', true),
    );
  }

  /** All past scans, newest first (for a re-scan/trend view). */
  public getHistory(clientId: string): Observable<ContractAnalysis[]> {
    return this.http.get<ContractAnalysis[]>(`${this.base}/clients/${clientId}/history`);
  }

  /** Permanently deletes ALL stored analyses for a client (clears the Analyse tab). */
  public deleteAllForClient(clientId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/clients/${clientId}`);
  }

  /** Shares/unshares the analysis with the end client; returns the updated analysis. */
  public setSharedWithClient(analysisUid: string, shared: boolean): Observable<ContractAnalysis> {
    return this.http.patch<ContractAnalysis>(`${this.base}/${analysisUid}/share`, { shared });
  }

  /** Fetch one analysis by id. */
  public getById(analysisUid: string): Observable<ContractAnalysis> {
    return this.http.get<ContractAnalysis>(`${this.base}/${analysisUid}`);
  }

  /** Starts an async batch over the supplied client ids. Returns immediately with a batch handle. */
  public runBulk(clientIds: string[]): Observable<AnalysisBatch> {
    const body: BulkAnalysisRequest = { clientIds };
    return this.http.post<AnalysisBatch>(`${this.base}/clients/bulk`, body);
  }

  /** Single poll of batch progress. */
  public getBatch(batchUid: string): Observable<AnalysisBatch> {
    return this.http.get<AnalysisBatch>(`${this.base}/batches/${batchUid}`);
  }

  /**
   * Polls a batch every `intervalMs` and emits each progress snapshot, completing once the
   * batch is no longer RUNNING/PENDING. Each client's result is then available via getLatest().
   */
  public pollBatch(batchUid: string, intervalMs = 4000): Observable<AnalysisBatch> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getBatch(batchUid)),
      takeWhile((batch): boolean => batch.status === 'RUNNING' || batch.status === 'PENDING', true),
    );
  }

  /** Branded PDF report as a Blob. */
  public downloadReport(analysisUid: string): Observable<Blob> {
    return this.http.get(`${this.base}/${analysisUid}/report`, { responseType: 'blob' });
  }

  /**
   * Sends the branded PDF report to the client as a chat message (advisor → client).
   * The recipient is resolved server-side from the analysis; only the message text is sent.
   */
  public shareReportViaChat(analysisUid: string, message: string): Observable<ContractAiShareReportResponse> {
    const body: ContractAiShareReportRequest = { message };
    return this.http.post<ContractAiShareReportResponse>(`${this.base}/${analysisUid}/report/chat`, body);
  }
}
